import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Code2, Database, BarChart3, Brain, Layers, Bot, Check } from 'lucide-react';

const NAVY = '#293891';
const RED = '#E63329';
const C = 260;
const R = 185;

const SKILLS = [
  { label: 'Python', icon: Code2 },
  { label: 'SQL', icon: Database },
  { label: 'Statistics', icon: BarChart3 },
  { label: 'Machine Learning', icon: Brain },
  { label: 'Deep Learning', icon: Layers },
  { label: 'Gen & Agentic AI', icon: Bot },
].map((s, i) => {
  const a = (-90 + i * 60) * (Math.PI / 180);
  return { ...s, x: C + R * Math.cos(a), y: C + R * Math.sin(a) };
});

const LOOP = ['Ask', 'Wrangle', 'Model', 'Deploy'];
const BUILDS = ['Churn predictor', 'Sales forecaster', 'RAG chatbot'];

function Spokes() {
  return (
    <svg viewBox="0 0 520 520" className="absolute inset-0 w-full h-full" aria-hidden="true">
      <circle cx={C} cy={C} r={R} fill="none" stroke={NAVY} strokeOpacity="0.12" strokeDasharray="3 7" />
      <circle cx={C} cy={C} r={110} fill="none" stroke={RED} strokeOpacity="0.15" />
      {SKILLS.map((s, i) => (
        <g key={s.label}>
          <line x1={C} y1={C} x2={s.x} y2={s.y} stroke={NAVY} strokeOpacity="0.18" strokeWidth="1.5" />
          <line x1={s.x} y1={s.y} x2={C} y2={C} stroke={i % 2 ? NAVY : RED} strokeWidth="1.5" className="flow-dash" style={{ animationDelay: `${i * 0.3}s` }} />
          <circle r="4" fill={i % 2 ? NAVY : RED}>
            <animateMotion dur={`${2.4 + (i % 3) * 0.4}s`} begin={`${i * 0.35}s`} repeatCount="indefinite" path={`M${s.x},${s.y} L${C},${C}`} />
            <animate attributeName="opacity" values="0;1;1;0" dur={`${2.4 + (i % 3) * 0.4}s`} begin={`${i * 0.35}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
    </svg>
  );
}

function Core() {
  return (
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-testid="hero-ai-core">
      <span className="absolute inset-0 rounded-full bg-[#E63329]/25 core-ping" />
      <span className="absolute inset-0 rounded-full bg-[#293891]/20 core-ping" style={{ animationDelay: '1.2s' }} />
      <div className="relative w-[104px] h-[104px] rounded-full bg-[#293891] flex flex-col items-center justify-center shadow-[0_20px_50px_-10px_rgba(41,56,145,0.6)] ring-8 ring-white">
        <span className="font-display text-white text-[34px] font-semibold leading-none">AI</span>
        <span className="text-[8.5px] tracking-[0.22em] text-white/75 mt-1 font-semibold">CORE</span>
      </div>
    </div>
  );
}

function SkillNode({ s, i }) {
  const Icon = s.icon;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
      className="absolute -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${(s.x / 520) * 100}%`, top: `${(s.y / 520) * 100}%` }}
      data-testid={`hero-skill-node-${i}`}
    >
      <div className="flex items-center gap-2 bg-white border border-[#293891]/15 rounded-full pl-1.5 pr-3 py-1.5 shadow-md whitespace-nowrap">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center ${i % 2 ? 'bg-[#293891]' : 'bg-[#E63329]'}`}>
          <Icon size={14} className="text-white" />
        </span>
        <span className="text-[11.5px] font-semibold text-[#293891]">{s.label}</span>
      </div>
    </motion.div>
  );
}

function AnalystLoop() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(p => (p + 1) % LOOP.length), 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="absolute top-0 left-0 origin-top-left scale-[0.85] min-[480px]:scale-100">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="animate-float-soft bg-white/95 backdrop-blur border border-[#293891]/10 rounded-2xl shadow-xl px-4 py-3 w-[190px]" data-testid="hero-widget-analyst-loop">
        <div className="text-[9.5px] tracking-[0.2em] font-bold text-[#E63329]">ANALYST LOOP</div>
        <div className="mt-2 grid grid-cols-4 gap-1">
          {LOOP.map((l, i) => (
            <div key={l} className="flex flex-col items-center gap-1">
              <span className={`h-1.5 w-full rounded-full transition-colors duration-500 ${i <= step ? 'bg-[#293891]' : 'bg-[#293891]/15'}`} />
              <span className={`text-[9.5px] font-semibold transition-colors duration-500 ${i === step ? 'text-[#E63329]' : 'text-[#293891]/60'}`}>{l}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function LiveData() {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }} className="absolute top-0 right-0 animate-float-soft bg-white/95 backdrop-blur border border-[#293891]/10 rounded-2xl shadow-xl px-4 py-3 w-[160px]" style={{ animationDelay: '1s' }} data-testid="hero-widget-live-data">
      <div className="flex items-center justify-between">
        <span className="text-[9.5px] tracking-[0.2em] font-bold text-[#293891]">LIVE DATA</span>
        <span className="w-2 h-2 rounded-full bg-[#E63329] animate-pulse" />
      </div>
      <div className="mt-2 flex items-end gap-1 h-10">
        {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
          <span key={i} className={`flex-1 rounded-sm live-bar ${i % 3 === 2 ? 'bg-[#E63329]' : 'bg-[#293891]'}`} style={{ animationDelay: `${i * 0.18}s` }} />
        ))}
      </div>
    </motion.div>
  );
}

function YouBuild() {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3 }} className="absolute bottom-0 left-0 animate-float-soft bg-[#293891] rounded-2xl shadow-xl px-4 py-3 w-[170px]" style={{ animationDelay: '2s' }} data-testid="hero-widget-you-build">
      <div className="text-[9.5px] tracking-[0.2em] font-bold text-white/70">YOU BUILD</div>
      <ul className="mt-2 space-y-1.5">
        {BUILDS.map(b => (
          <li key={b} className="flex items-center gap-2 text-[11px] font-semibold text-white">
            <span className="w-4 h-4 rounded-full bg-[#E63329] flex items-center justify-center"><Check size={10} /></span>{b}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function HeroAI() {
  return (
    <div className="relative w-full h-full flex items-center justify-center" data-testid="hero-ai-network">
      <div className="absolute top-6 right-6 w-72 h-72 rounded-full bg-[#E63329]/10 blur-3xl" />
      <div className="absolute bottom-4 left-2 w-72 h-72 rounded-full bg-[#293891]/15 blur-3xl" />
      <div className="relative w-[520px] h-[520px] shrink-0 scale-[0.62] min-[480px]:scale-[0.85] sm:scale-100">
        <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }} className="absolute inset-0">
          <Spokes />
          <Core />
          {SKILLS.map((s, i) => <SkillNode key={s.label} s={s} i={i} />)}
        </motion.div>
        <AnalystLoop />
        <LiveData />
        <YouBuild />
      </div>
    </div>
  );
}
